import { RpcError } from "grpc-web";
import { HasPaymentMethodResponse } from "../../../proto/api_pb";
import { MasterMetadata } from "../../../proto/api_pb";
import { Track, Collaborator, TrackState } from "../../../proto/api_pb";
import { TrackType, TrackVersion } from "../../../proto/api_pb";
import {  postTrackVersion} from "../../../services/trackService";
import {  fetchLatestVersion} from "../../../services/trackService";
import { fetchTrackVersions, postTrack } from "../../../services/trackService";
import { getHasPaymentMethod } from "../../../services/stripeService";
import { handleDolbyPreviewEffect } from "../../../services/dolbyService";
import handleDolbyEffect from "../../../services/dolbyService";
import { awaitableUploadToS3 } from "../../../utils/S3Utils";
import { mapToTalkUser } from "../../../utils/TalkjsUtils";
import { TalkJsContextInterface } from "../../../contexts";
import Mixpanel from "../../../utils/mixpanel";
import { toast } from "react-toastify";

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

export const downloadSong = async (
  trackUrl: string,
  title: string | undefined
): Promise<void> => {
  try {
    const response = await fetch(trackUrl);
    const songBlob = await response.blob();
    const songUrl = URL.createObjectURL(songBlob);
    const link = document.createElement("a");
    link.href = songUrl;
    link.download = title!;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.log("Download Error");
    console.log(error);
  }
};

export const handleFetchTrackVersions = async (
  token: string,
  id: string
): Promise<
  | {
      track: TrackVersion;
      isPlaying: boolean;
    }[]
  | undefined
> => {
  try {
    const response = await fetchTrackVersions(token, id!);
    if (!response) return undefined;
    if (response instanceof RpcError) return undefined;
    const newTrackVersionList = response.getTrackVersionsList().map((el) => {
      return { track: el, isPlaying: false };
    });
    return newTrackVersionList;
  } catch (e) {
    const error = e as RpcError;
    console.log(error.code);
    console.log(error.message);
  }
};

export const fetchBpmfromVersion = async (
  token: string,
  versionId: string
): Promise<number | undefined> => {
  const response = await fetchLatestVersion(token, versionId!);
  if (!response) return undefined;
  if (response instanceof RpcError) {
    return undefined;
  } else {
    const responseBpm = response
      .getTrackVersion()
      ?.getAudioMetadata()
      ?.getBpm();

    return responseBpm;
  }
};

export const getPaymentMethod = async (
  token: string,
  userId: string
): Promise<HasPaymentMethodResponse | undefined> => {
  try {
    const response = (await getHasPaymentMethod(
      token,
      userId!
    )) as HasPaymentMethodResponse;
    return response;
  } catch (e) {
    const error = e as RpcError;
    console.log(error.code);
    console.log(error.message);
  }
};

const handleError = () => {
  toast.error("Error");
};

export const addDolbyEffect = (
  selectedPresetArray: string[],
  sliderValue: number,
  token: string,
  trackUrl: string,
  uuid: string,
  setPreviewUrl: (url: Map<string, string>) => void,
  setPreviewLoading: (loading: boolean) => void,
  handlePreviewState: (state: string) => void,
  previewStartValue: number | undefined,
  duration: number | undefined
): void => {
  handleDolbyPreviewEffect(
    token,
    handleError,
    setPreviewUrl,
    setPreviewLoading,
    handlePreviewState,
    selectedPresetArray,
    trackUrl,
    uuid,
    sliderValue,
    previewStartValue,
    duration
  );
};

export const handleAddTrackVersion = (
  selectedPresetValue: string,
  sliderValue: number,
  token: string,
  trackUrl: string,
  uuid: string,
  setMasteredMusicUrl: (url: string) => void,
  setPreviewLoading: (loading: boolean) => void,
  handleState: (state: string) => void
): void => {
  handleDolbyEffect(
    token,
    handleError,
    setMasteredMusicUrl,
    setPreviewLoading,
    handleState,
    selectedPresetValue,
    trackUrl,
    uuid,
    sliderValue
  );
};

export const addNewVersion = async (
  track: Track | undefined,
  masteredMusicUrl: string | undefined,
  selectedPreset: string,
  jobId: string | undefined,
  previewUrl: string | undefined,
  token: string,
  id: string
): Promise<
  | {
      track: TrackVersion;
      isPlaying: boolean;
    }[]
  | undefined
> => {
  const newTrack = track;
  if (newTrack && masteredMusicUrl) {
    newTrack.setOwnerId(track.getOwnerId());
    newTrack.setArtist(track.getArtist());
    newTrack.setBpm(track.getBpm());
    newTrack.setDescription(track.getDescription());
    newTrack.setImageUrl(track.getImageUrl());
    newTrack.setTitle(track.getTitle());
    newTrack.setTrackType(TrackType.TRACK_TYPE_SONG);
    newTrack.setState(TrackState.TRACK_STATE_DRAFT);
    newTrack.setSongMetadata(track.getSongMetadata());
    newTrack.setCollaboratorsList(track.getCollaboratorsList());
    newTrack.setKey(track.getKey());

    const trackVersion = new TrackVersion();
    // TODO: expose label entry field here for adding comment/notes/name
    trackVersion.setLabel(`Mastered by dolby with preset: ${selectedPreset}`);
    trackVersion.setTrackUrl(masteredMusicUrl);
    trackVersion.setTrackId(track.getId());
    if (jobId && masteredMusicUrl && previewUrl && selectedPreset) {
      const masterMetadata = new MasterMetadata();
      masterMetadata.setId(track.getId());
      masterMetadata.setState(2);
      masterMetadata.setJobId(jobId);
      masterMetadata.setDolbyMasterUrl(masteredMusicUrl);
      masterMetadata.setDolbyPreviewUrl(previewUrl);
      masterMetadata.setLoudnessProfile(selectedPreset);
      masterMetadata.setMasteringProfile(selectedPreset);
      masterMetadata.setTimestamp(Date.now());
      trackVersion.setMasterMetadata(masterMetadata);
    }
    const postTrackVersionResponse = await postTrackVersion(
      trackVersion,
      token
    );
    if (postTrackVersionResponse instanceof RpcError) {
      //TODO: handle api exception gracefully with error modal, etc
      throw postTrackVersionResponse;
    }
    track.setTrackVersionId(
      postTrackVersionResponse.getTrackVersion()!.getId()
    );
    const postTrackResponse = await postTrack(track, token);
    if (postTrackResponse instanceof RpcError) {
      //TODO: handle api exception gracefully with error modal, etc
      throw postTrackResponse;
    }
  }

  const response = await handleFetchTrackVersions(token, id!);
  return response;
};

export const onDownload = async (
  file: File,
  awsAccessKeyId: string | undefined,
  awsSecretAccessKey: string | undefined,
  awsSessionToken: string | undefined,
  userId: string
): Promise<string> => {
  const masteredMusicKey = await awaitableUploadToS3(
    userId,
    file,
    `song`,
    file.type,
    awsAccessKeyId,
    awsSecretAccessKey,
    awsSessionToken,
    () => {}
  );
  const finalMasteredMusicUrl = ASSETS_URL + "/" + masteredMusicKey;
  console.log("mastered song", finalMasteredMusicUrl);
  return finalMasteredMusicUrl;
};

export const onMasterDownload = async (
  file: File,
  awsAccessKeyId: string | undefined,
  awsSecretAccessKey: string | undefined,
  awsSessionToken: string | undefined,
  userId: string
): Promise<string> => {
  const masteredMusicKey = await awaitableUploadToS3(
    userId,
    file,
    `song`,
    file.type,
    awsAccessKeyId,
    awsSecretAccessKey,
    awsSessionToken,
    () => {}
  );
  const finalMasteredMusicUrl = ASSETS_URL + "/" + masteredMusicKey;
  console.log("mastered song", finalMasteredMusicUrl);
  return finalMasteredMusicUrl;
};

export const handleCreateGroupChat = (
  talkJsContext: TalkJsContextInterface,
  contributors: Collaborator[] | undefined,
  navigate: (path: string) => void,
  setCreateGroupChatClicked: React.Dispatch<React.SetStateAction<boolean>>,
  id?: string | undefined,
  title?: string | undefined
): void => {
  try {
    if (talkJsContext && contributors) {
      Mixpanel.track("open_group_chat");
      const participants = contributors
        .filter((c) => c.getUsername())
        .map(mapToTalkUser);
      if (window.innerWidth > 450) {
        talkJsContext.handleChatClick();
        talkJsContext.setParticipants(participants);
        setCreateGroupChatClicked(true);
      } else {
        talkJsContext.setParticipants(participants);
        talkJsContext.setAutoCreateChat(true);
        if (id && title) navigate(`/messages/${id}/${title}`);
        else navigate("/messages");
      }
    }
  } catch (error) {
    console.log(error);
  }
};
